/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../platform/Platform';
import {
  AliasType,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../../registry/Component.type';
import { ItemType } from '../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../registry/Components';
import { defaultValue } from './Tracker';

const TrackerManager: ComponentManagerFn<'Tracker'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Tracker'>(platform, 'Tracker'),
    type: 'Tracker',
    name: 'Tracker',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        category: 'Data',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
          'TransientDataStore',
        ],
      },
      idAttribute: {
        label: 'ID Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String', 'Integer'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      nameAttribute: {
        label: 'Name Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      xAttribute: {
        label: 'X Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Integer', 'Double'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      yAttribute: {
        label: 'Y Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Integer', 'Double'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      tracking: {
        label: 'Tracking',
        type: 'boolean',
      },
    },
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'STORE' ? { aliasProp: 'alias' } : undefined,
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'idAttribute':
        case 'nameAttribute':
        case 'xAttribute':
        case 'yAttribute':
          return !!itemData.alias;
        default:
          return true;
      }
    },
    replaceableWith: ['Device', 'Area', 'Pulse'],
    getDisplayText: (item) => item.props.nameAttribute,
    category: 'IoT',
    icon: { iconName: 'draw-polygon' },
    description: `**Overview**<br/>
The Tracker component is a dynamic visualization tool for the **IoT Image Mapper** designed to monitor moving assets or changing positions in real-time. While similar to the **Device** component in that it plots points on a map, the Tracker is specifically optimized for scenarios where coordinates are expected to update frequently, representing fluid movement across a floor plan.
Use this component for live fleet management, indoor positioning systems (IPS), or tracking personnel movement within a facility.

**1. Data Connection**<br/>
The component binds to a Data Store to retrieve the changing location data of the assets being tracked.
* **Alias:** The Data Store containing the live tracking records.
* **ID Attribute:** The unique identifier for the moving asset (e.g., "Vehicle-ID-04").
* **Name Attribute:** The label displayed for the asset (e.g., "Forklift 4").

**2. Positioning**<br/>
* **X Attribute & Y Attribute:** These fields (Integer or Double) dictate the current position of the asset.
* **Auto-Update:** As the data in the underlying store changes (e.g., via a websocket or polling), the Tracker component automatically updates the marker's position on the canvas.

**3. Features**<br/>
* **Tracking Mode:** Enables specialized behaviors for moving objects, such as smoothing animations between coordinate updates to prevent "jumping" or leaving a visual "trail" of previous positions.
* **Interactivity:** Supports adding a **TriggerOnClick** action. Clicking a moving tracker can open a modal with live details like current speed, battery level, or operator information.

**4. Component Comparison**<br/>
* **Tracker:** Used for **Movement**. Optimized for live updates of position (people, vehicles) with frequent coordinate changes and paths.
* **Device:** Used for **Stationary Assets**. Best for fixed equipment like cameras or sensors using static icons to indicate device type.
* **Pulse:** Used for **Critical Events**. Features animated radiating rings to grab immediate attention for alarms or emergency calls.

**5. Best Use Cases**<br/>
* **Indoor Positioning:** Tracking the real-time location of medical staff or high-value equipment wearing Bluetooth beacons in a hospital.
* **Logistics & Warehousing:** Monitoring the movement of AGVs (Automated Guided Vehicles) or forklifts to optimize traffic flow.
* **Supply Chain:** Visualizing pallets or bins as they move through different production zones on a factory floor.`,
  };
};

export default TrackerManager;
